import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { 
  FileSpreadsheet, 
  Check,
  X,
  ArrowRight,
  Calculator,
  Zap,
  Clock,
  DollarSign,
  Users,
  Shield,
  Sparkles,
  TrendingDown,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const CompareHubSpotPageEN = () => {
  const [contactCount, setContactCount] = useState(10000);
  const [cleaningFrequency, setCleaningFrequency] = useState('monthly');
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // HubSpot pricing (annual)
  const hubspotPricing = {
    starter: { monthly: 9, name: 'Starter', features: ['Data sync', 'Custom field mappings', 'No duplicate management', 'No bulk cleaning'] },
    professional: { monthly: 720, name: 'Professional', features: ['Bulk duplicate management', 'Data quality overview', 'AI formatting recommendations'] },
    enterprise: { monthly: 2000, name: 'Enterprise', features: ['All Pro features', 'Advanced data calculations', 'Data warehouse integrations'] }
  };

  // CRM Data Cleaner pricing
  const crmCleanerPricing = {
    free: { monthly: 0, rows: 100, name: 'Free' },
    standard: { monthly: 19, rows: 5000, name: 'Standard' },
    pro: { monthly: 49, rows: 25000, name: 'Pro' },
    enterprise: { monthly: 99, rows: 100000, name: 'Enterprise' }
  };

  // Calculate costs
  const cleaningsPerYear = cleaningFrequency === 'monthly' ? 12 : cleaningFrequency === 'quarterly' ? 4 : 1;
  const rowsPerYear = contactCount * cleaningsPerYear;

  // Find best CRM Data Cleaner plan
  const getBestPlan = () => {
    const monthlyRows = contactCount;
    if (monthlyRows <= 100) return 'free';
    if (monthlyRows <= 5000) return 'standard';
    if (monthlyRows <= 25000) return 'pro';
    return 'enterprise';
  };

  const bestPlan = getBestPlan();
  const crmCleanerYearlyCost = crmCleanerPricing[bestPlan].monthly * 12;
  const hubspotProYearlyCost = hubspotPricing.professional.monthly * 12;
  const savings = hubspotProYearlyCost - crmCleanerYearlyCost;
  const savingsPercent = Math.round((savings / hubspotProYearlyCost) * 100);

  // Feature comparison data
  const featureComparison = [
    { 
      category: 'Data Cleaning',
      features: [
        { name: 'Automatic email typo correction', crm: true, hubspot: false, hubspotNote: 'Manual only' },
        { name: 'Phone number standardization', crm: true, hubspot: true, hubspotNote: 'Via Operations Hub Pro' },
        { name: 'Name normalization (Title Case)', crm: true, hubspot: true, hubspotNote: 'Via workflows' },
        { name: 'Extract first/last name from email', crm: true, hubspot: false },
        { name: 'Gender detection from first name', crm: true, hubspot: false },
        { name: 'Revenue normalization', crm: true, hubspot: false },
        { name: 'Dropdown value standardization', crm: true, hubspot: true, hubspotNote: 'Manual configuration' },
        { name: 'Bulk cleaning', crm: true, hubspot: true, hubspotNote: 'Pro+ only ($720/mo)' },
      ]
    },
    {
      category: 'Duplicate Management',
      features: [
        { name: 'Duplicate detection', crm: true, hubspot: true, hubspotNote: 'Pro+ only' },
        { name: 'Automatic merging', crm: true, hubspot: true, hubspotNote: 'Pro+ only' },
        { name: 'Preview before merge', crm: true, hubspot: true },
      ]
    },
    {
      category: 'Data Validation',
      features: [
        { name: 'Email MX record verification', crm: true, hubspot: false },
        { name: 'Invalid email detection', crm: true, hubspot: false },
        { name: 'Phone format validation', crm: true, hubspot: false },
      ]
    },
    {
      category: 'Ease of Use',
      features: [
        { name: 'Direct CSV/Excel import', crm: true, hubspot: false, hubspotNote: 'Requires HubSpot import' },
        { name: 'Preview changes before applying', crm: true, hubspot: false },
        { name: 'Export cleaned file', crm: true, hubspot: false, hubspotNote: 'Data stays in HubSpot' },
        { name: 'Use without CRM account', crm: true, hubspot: false },
        { name: 'Simple interface', crm: true, hubspot: true },
      ]
    },
    {
      category: 'Pricing',
      features: [
        { name: 'Free plan available', crm: true, hubspot: true, hubspotNote: 'Limited features' },
        { name: 'Pay-as-you-go option', crm: true, hubspot: false },
        { name: 'No annual commitment required', crm: true, hubspot: false },
        { name: 'Transparent pricing', crm: true, hubspot: false, hubspotNote: 'Requires sales contact' },
      ]
    }
  ];

  const displayedFeatures = showAllFeatures ? featureComparison : featureComparison.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
            <TrendingDown className="w-4 h-4" />
            Save up to {savingsPercent}% vs HubSpot Data Hub
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            CRM Data Cleaner vs<br />
            <span className="text-orange-500">HubSpot Operations Hub</span>
          </h1>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8">
            Compare data cleaning features and discover why thousands of teams 
            choose CRM Data Cleaner to prepare their data before CRM import.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Try for free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing/subscription">
              <Button size="lg" variant="outline" className="text-lg px-8">
                View pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Comparison Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* CRM Data Cleaner Card */}
            <div className="relative bg-gradient-to-br from-indigo-50 to-white rounded-2xl border-2 border-indigo-200 p-8 shadow-lg">
              <div className="absolute -top-4 left-6 bg-indigo-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                Recommended
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <FileSpreadsheet className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">CRM Data Cleaner</h3>
                  <p className="text-slate-500">Clean before import</p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-indigo-600">
                    ${crmCleanerPricing[bestPlan].monthly}
                  </span>
                  <span className="text-slate-500">/month</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {crmCleanerPricing[bestPlan].name} plan • Up to {crmCleanerPricing[bestPlan].rows.toLocaleString()} rows/month
                </p>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  'Instant cleaning in 3 seconds',
                  'Automatic email typo correction',
                  'AI-powered name extraction from email',
                  'Preview before applying changes',
                  'Export CSV/Excel ready for import',
                  'No HubSpot account needed'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/register">
                <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                  Start for free
                </Button>
              </Link>
            </div>

            {/* HubSpot Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-xl">H</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900">HubSpot Operations Hub</h3>
                  <p className="text-slate-500">Clean inside CRM</p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-slate-900">$720</span>
                  <span className="text-slate-500">/month</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Professional plan • Minimum for bulk cleaning
                </p>
              </div>

              <ul className="space-y-3 mb-6">
                {[
                  { text: 'Duplicate management', included: true },
                  { text: 'AI formatting recommendations', included: true },
                  { text: 'Data quality overview', included: true },
                  { text: 'Automatic email correction', included: false },
                  { text: 'Name extraction from email', included: false },
                  { text: 'Clean before import', included: false },
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-700">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-red-400 flex-shrink-0" />
                    )}
                    <span className={!feature.included ? 'text-slate-400' : ''}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <Button variant="outline" className="w-full" disabled>
                Requires HubSpot subscription
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Calculator */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Calculator className="w-4 h-4" />
              Savings Calculator
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How much can you save?
            </h2>
            <p className="text-slate-600">
              Adjust the settings to see your potential savings
            </p>
          </div>

          <div className="bg-white rounded-2xl border shadow-lg p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Number of contacts to clean
                </label>
                <input
                  type="range"
                  min="1000"
                  max="100000"
                  step="1000"
                  value={contactCount}
                  onChange={(e) => setContactCount(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-2xl font-bold text-indigo-600">
                    {contactCount.toLocaleString()}
                  </span>
                  <span className="text-slate-500">contacts</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cleaning frequency
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'yearly', label: 'Yearly' }
                  ].map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => setCleaningFrequency(freq.value)}
                      className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                        cleaningFrequency === freq.value
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {freq.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="grid md:grid-cols-3 gap-6 pt-8 border-t">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-500 mb-1">HubSpot Operations Hub Pro</p>
                <p className="text-3xl font-bold text-slate-900">
                  ${hubspotProYearlyCost.toLocaleString()}
                </p>
                <p className="text-sm text-slate-500">/year</p>
              </div>

              <div className="text-center p-4 bg-indigo-50 rounded-xl border-2 border-indigo-200">
                <p className="text-sm text-indigo-600 mb-1">CRM Data Cleaner {crmCleanerPricing[bestPlan].name}</p>
                <p className="text-3xl font-bold text-indigo-600">
                  ${crmCleanerYearlyCost.toLocaleString()}
                </p>
                <p className="text-sm text-indigo-500">/year</p>
              </div>

              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <p className="text-sm text-emerald-600 mb-1">Your savings</p>
                <p className="text-3xl font-bold text-emerald-600">
                  ${savings.toLocaleString()}
                </p>
                <p className="text-sm text-emerald-500">/year ({savingsPercent}%)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Feature Comparison Table */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Detailed Feature Comparison
            </h2>
            <p className="text-slate-600">
              Discover what sets CRM Data Cleaner apart from HubSpot Operations Hub
            </p>
          </div>

          <div className="bg-white rounded-2xl border shadow-lg overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-3 bg-slate-50 border-b">
              <div className="p-4 font-semibold text-slate-700">Feature</div>
              <div className="p-4 font-semibold text-center bg-indigo-50 text-indigo-700">
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  CRM Data Cleaner
                </div>
              </div>
              <div className="p-4 font-semibold text-center text-slate-700">
                <div className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 bg-orange-500 rounded text-white text-xs flex items-center justify-center font-bold">H</span>
                  HubSpot
                </div>
              </div>
            </div>

            {/* Table Body */}
            {displayedFeatures.map((category, catIdx) => (
              <div key={catIdx}>
                <div className="bg-slate-100 px-4 py-2 font-semibold text-slate-800 border-b">
                  {category.category}
                </div>
                {category.features.map((feature, featIdx) => (
                  <div 
                    key={featIdx} 
                    className={`grid grid-cols-3 border-b last:border-b-0 ${
                      featIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    <div className="p-4 text-slate-700">{feature.name}</div>
                    <div className="p-4 flex justify-center bg-indigo-50/30">
                      {feature.crm ? (
                        <Check className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <X className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div className="p-4 flex flex-col items-center">
                      {feature.hubspot ? (
                        <>
                          <Check className="w-5 h-5 text-emerald-500" />
                          {feature.hubspotNote && (
                            <span className="text-xs text-slate-400 mt-1 text-center">
                              {feature.hubspotNote}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <X className="w-5 h-5 text-red-400" />
                          {feature.hubspotNote && (
                            <span className="text-xs text-slate-400 mt-1 text-center">
                              {feature.hubspotNote}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Show More Button */}
          <div className="text-center mt-6">
            <Button 
              variant="outline" 
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className="gap-2"
            >
              {showAllFeatures ? (
                <>
                  Show less
                  <ChevronUp className="w-4 h-4" />
                </>
              ) : (
                <>
                  Show all features
                  <ChevronDown className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-indigo-600">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Why choose CRM Data Cleaner?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: DollarSign,
                title: '98% cheaper',
                description: 'Starting at $19/month vs $720/month for HubSpot Operations Hub Pro'
              },
              {
                icon: Zap,
                title: 'Instant cleaning',
                description: '3 seconds to clean 1,000 rows. No need to wait for HubSpot workflows.'
              },
              {
                icon: Shield,
                title: 'Before import',
                description: 'Clean your data BEFORE polluting your CRM with dirty data.'
              }
            ].map((benefit, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-6 text-center">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-indigo-100">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Box */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8">
            <div className="flex gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-500 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-semibold text-amber-900 mb-2">
                  The problem with cleaning INSIDE the CRM
                </h3>
                <p className="text-amber-800 mb-4">
                  HubSpot Operations Hub cleans your data <strong>after</strong> it's imported into the CRM. 
                  This means your dirty data is already in your system, creating:
                </p>
                <ul className="space-y-2 text-amber-800">
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    Duplicates that trigger unwanted workflows
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    Emails sent to invalid addresses (hurting deliverability)
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    Polluted historical data that's hard to fix
                  </li>
                </ul>
                <p className="text-amber-800 mt-4 font-medium">
                  With CRM Data Cleaner, clean <strong>BEFORE</strong> import and keep your CRM pristine from day one.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to save ${savings.toLocaleString()}/year?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Start free with 100 credits. No credit card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-lg px-8">
                Try for free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing/subscription">
              <Button size="lg" variant="outline" className="text-lg px-8 border-slate-600 text-white hover:bg-slate-800">
                View pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center text-slate-400 text-sm">
          <p>
            HubSpot pricing is based on publicly available rates on hubspot.com as of January 2026.
            Feature comparison is based on official HubSpot documentation.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CompareHubSpotPageEN;
